import { getError } from "@/utils/get-error";
import { MESApiService, axiosInstance } from "@dexteel/mesf-core";
import { Axios } from "axios";
import { ComponentType } from "../../componentTypes/models/ComponentType";
import { Part } from "../models/Part";

export class PartsRepository {
	private axios: Axios;
	constructor() {
		this.axios = axiosInstance;

		this.getParts = this.getParts.bind(this);
		this.getPartById = this.getPartById.bind(this);
		this.createPart = this.createPart.bind(this);
		this.updatePart = this.updatePart.bind(this);
		this.updateComponentTypeByPart = this.updateComponentTypeByPart.bind(this);
		this.deletePart = this.deletePart.bind(this);
	}

	async getParts() {
		try {
			const resp = await this.axios.get("/Part/");

			return {
				ok: true,
				data: resp,
			};
		} catch (error: any) {
			const message = getError(error);

			return Promise.reject(message);
		}
	}

	async getPartById(partId: number | null) {
		try {
			const resp = await this.axios.get(`/Part/${partId}`);
			return {
				ok: true,
				data: resp,
			};
		} catch (e: any) {
			return { ok: false, message: e.toString() };
		}
	}
	async createPart({
		partName,
		description,
		sku,
		unit,
		vendorId,
		actualArrival,
		isActive,
		componentTypeIds,
	}: Part) {
		try {
			const resp = await this.axios.post("/Part/", {
				partName: partName.trim(),
				description: description.trim(),
				sku: sku.trim(),
				unit: unit.trim(),
				vendorId,
				actualArrival,
				isActive,
				// The backend takes them comma-separated, same as updateComponentTypeByPart
				componentTypeIds: componentTypeIds.toString(),
			});
			return {
				ok: true,
				resp,
			};
		} catch (error: any) {
			const message = getError(error);

			return Promise.reject(message);
		}
	}

	// Explicit whitelist: the form object also carries partId and componentTypeIds, which
	// UpdatePartModel does not accept — sending them is noise at best, and a part with no
	// component types if someone ever wires that field up.
	async updatePart(partId: number | null, data: Part) {
		try {
			const resp = await this.axios.patch(`/Part/${partId}`, {
				partName: data.partName.trim(),
				description: data.description.trim(),
				sku: data.sku.trim(),
				unit: data.unit.trim(),
				vendorId: data.vendorId,
				isActive: data.isActive,
			});
			return {
				ok: true,
			};
		} catch (error: any) {
			const message = getError(error);

			return Promise.reject(message);
		}
	}
	async updateComponentTypeByPart(partId: number | null, ids: any) {
		let idArray: Array<Number> = [];

		ids.forEach((ct: ComponentType) => idArray.push(ct.componentTypeId));

		try {
			const resp = await this.axios.patch(
				`/Part/${partId}/component-types`,
				null,
				{ params: { ids: idArray.toString() } },
			);

			return {
				ok: true,
				data: resp,
			};
		} catch (error: any) {
			const message = getError(error);

			return Promise.reject(message);
		}
	}

	async deletePart(partId: number | null) {
		try {
			const resp = await this.axios.delete("/Part/", {
				params: { partId },
			});
			return {
				ok: true,
				resp,
			};
		} catch (error: any) {
			const message = getError(error);

			return Promise.reject(message);
		}
	}

	async getPartHistory(partId: number) {
		const api = new MESApiService();
		return await api.callV2("REF.GetPartHistory", [
			{
				name: "PartId",
				value: partId,
			},
		]);
	}
}

export const {
	getParts,
	getPartById,
	createPart,
	updatePart,
	updateComponentTypeByPart,
	deletePart,
	getPartHistory,
} = new PartsRepository();
